import { isToolRemembered, rememberTool } from './toolPermissions';

// One agentic chat turn. The model runs on the server
// (/v1/conversation/chat/agent, one step per call); the tools run here, in the
// visit view, against the clinician's EMR tab. Each step either returns tool
// calls - run them, send back the results - or the final answer.
//
// Tools are { name, description, parameters, handler(args, ctx), permissionKey?,
// permissionLabel? }. A tool that changes anything calls
// ctx.requestPermission(preview) before it does; that resolves to true only when
// the clinician allows it (once, or always for that tool). A selectable review
// resolves to { selectedChanges } so the tool applies only what they kept.

export const MAX_STEPS = 10;

export const PERMISSION_DECISIONS = Object.freeze({
  once: 'once',
  always: 'always',
  deny: 'deny',
});

const toolSpec = ({ name, description, parameters }) => ({
  type: 'function',
  name,
  description,
  parameters,
});

function parseArguments(raw) {
  try {
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    return null;
  }
}

const newActionId = () => `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const newTurnId = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

// A step that never got an answer (network drop, timeout) is asked again once.
// That is safe: tools only ever run on an answer we received, and the server
// saves a turn once per turnId, handing back the saved answer to a retry.
export const STEP_RETRY_DELAY_MS = 1500;
async function stepWithRetry(send, delayMs) {
  try {
    return await send();
  } catch (err) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return send();
  }
}

export async function runAgentTurn({
  apiClient,
  conversationId,
  question,
  tools,
  userEmail,
  requestPermission,
  onActivity = () => {},
  isCancelled = () => false,
  // Earlier turns' changes, by action id (saved with the chat), for undo_change
  findAction = () => null,
  // What the model should know about this visit up front (what it can't do here, and why)
  context = [],
  // ({question, options}) => Promise<{answer, choseOption} | {cancelled}>: clickable questions (ask_user)
  askUser = null,
  retryDelayMs = STEP_RETRY_DELAY_MS,
}) {
  const byName = new Map(tools.map((tool) => [tool.name, tool]));
  const specs = tools.map(toolSpec);
  const turnMessages = [];
  const actions = [];
  const turnId = newTurnId();

  const permissionFor = (tool) => async (preview) => {
    const permissionKey = tool.permissionKey || tool.name;
    if (!preview.requireReview && isToolRemembered(userEmail, permissionKey)) {
      onActivity({ type: 'auto_allowed', tool: tool.name, preview });
      return true;
    }
    if (isCancelled()) return false;
    const answer = await requestPermission({
      tool: tool.name,
      permissionKey,
      permissionLabel: tool.permissionLabel || tool.name,
      ...preview,
    });
    const decision = typeof answer === 'object' ? answer?.decision : answer;
    if (decision === PERMISSION_DECISIONS.always) {
      rememberTool(userEmail, permissionKey, tool.permissionLabel || tool.name);
      onActivity({ type: 'remembered', tool: tool.name, permissionKey });
    }
    const allowed = decision === PERMISSION_DECISIONS.once || decision === PERMISSION_DECISIONS.always;
    if (!allowed) return false;
    return Array.isArray(answer?.selectedChanges)
      ? { selectedChanges: answer.selectedChanges }
      : true;
  };

  const runTool = async (call) => {
    const tool = byName.get(call.name);
    if (!tool) return { ok: false, error: `Unknown tool ${call.name}` };
    const args = parseArguments(call.arguments);
    if (!args) return { ok: false, error: 'Arguments were not valid JSON' };
    onActivity({ type: 'tool_start', tool: call.name, args });
    try {
      const ctx = {
        requestPermission: permissionFor(tool),
        isCancelled,
        askUser: askUser && ((request) => (isCancelled() ? Promise.resolve({ cancelled: true }) : askUser(request))),
        findAction: (id) => actions.find((action) => action.id === id) || findAction(id),
      };
      const result = (await tool.handler(args, ctx)) || {};
      if (result.action) {
        // An id the model can name later ("undo that") and the chat keeps with its record
        result.action = { id: newActionId(), tool: call.name, ...result.action };
        actions.push(result.action);
      }
      onActivity({ type: 'tool_end', tool: call.name, result });
      return result;
    } catch (err) {
      const result = { ok: false, error: err?.message || 'The tool failed' };
      onActivity({ type: 'tool_end', tool: call.name, result });
      return result;
    }
  };

  for (let step = 0; step < MAX_STEPS; step += 1) {
    if (isCancelled()) return { cancelled: true, actions };
    // eslint-disable-next-line no-await-in-loop
    const rsp = await stepWithRetry(() =>
      apiClient.chatAgentStep(conversationId, question, specs, turnMessages, actions, context, turnId),
      retryDelayMs,
    );
    if (!rsp || rsp.error) {
      throw new Error(typeof rsp?.error === 'string' ? rsp.error : 'The assistant could not respond.');
    }
    if (!Array.isArray(rsp.toolCalls) || rsp.toolCalls.length === 0) {
      return { response: rsp.response || '', actions: rsp.actions || actions, saved: rsp.saved };
    }
    turnMessages.push({
      role: 'assistant',
      content: rsp.content || null,
      tool_calls: rsp.toolCalls.map((call) => ({
        id: call.id,
        type: 'function',
        function: { name: call.name, arguments: call.arguments || '{}' },
      })),
    });
    // One at a time: tools share the EMR tab, and permission cards come in order
    for (const call of rsp.toolCalls) {
      // eslint-disable-next-line no-await-in-loop
      const result = isCancelled() ? { ok: false, error: 'Stopped by the clinician' } : await runTool(call);
      // The record (with its undo snapshot) is for the chat; the model gets its id and what changed
      const { action, ...forModel } = result;
      if (action) {
        forModel.action_id = action.id;
        if (action.detail) forModel.changed = action.detail;
        forModel.undoable = Boolean(action.undo);
      }
      turnMessages.push({ role: 'tool', tool_call_id: call.id, content: JSON.stringify(forModel) });
    }
  }
  return {
    response: 'I stopped before finishing: that took more steps than I allow in one turn.',
    actions,
    incomplete: true,
  };
}
